import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';

const PeopleYouMayKnowWidget = ({ limit = 3 }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingRequest, setSendingRequest] = useState({});

  useEffect(() => {
    if (user) {
      fetchSuggestions();
    }
  }, [user]);

  const fetchSuggestions = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/connections/smart-suggestions?limit=${limit}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const smartSuggestions = response.data.suggestions || [];
      setSuggestions(smartSuggestions.slice(0, limit));
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendConnectionRequest = async (userId, userName) => {
    setSendingRequest(prev => ({ ...prev, [userId]: true }));
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${backendUrl}/api/users/${userId}/connection-request`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Remove from suggestions
      setSuggestions(suggestions.filter(s => s.user.id !== userId));
      
      alert(`Connection request sent to ${userName}!`);
    } catch (error) {
      console.error('Error sending connection request:', error);
      alert(error.response?.data?.detail || 'Error sending connection request');
    } finally {
      setSendingRequest(prev => ({ ...prev, [userId]: false }));
    }
  };

  if (!user || loading) {
    return null;
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">👥 People You May Know</h2>
        <button
          onClick={() => navigate('/my-network', { state: { activeTab: 'suggestions' } })}
          className="text-sm text-teal-600 hover:text-teal-700 font-medium"
        >
          View All
        </button>
      </div>
      
      <div className="space-y-4">
        {suggestions.map((suggestion) => {
          const user = suggestion.user;
          const reasons = suggestion.reasons || [];
          const topReason = reasons[0];
          
          return (
            <div key={user.id} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                {user.full_name.charAt(0)}
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm truncate">{user.full_name}</h3>
                {user.company_name && (
                  <p className="text-xs text-gray-600 truncate">{user.company_name}</p>
                )}
                
                {topReason && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                    <span>{topReason.icon}</span>
                    <span className="truncate">{topReason.details}</span>
                  </div>
                )}
                
                <button
                  onClick={() => sendConnectionRequest(user.id, user.full_name)}
                  disabled={sendingRequest[user.id]}
                  className={`mt-2 w-full text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                    sendingRequest[user.id]
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  {sendingRequest[user.id] ? '⏳ Sending...' : '🤝 Connect'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500 text-center">
          Suggestions based on mutual connections, shared groups, and interests
        </p>
      </div>
    </div>
  );
};

export default PeopleYouMayKnowWidget;
